// Admin2FASetup.tsx — F6 T6.7.
//
// Flow:
//   1. Na mount: wyciagnij setupToken z location.state (przesłany z Login.tsx po
//      response {requireSetup, setupToken}).
//   2. POST /auth/admin/2fa/setup (Bearer setupToken) → odczytaj {secret, qrDataUrl, backupCodes}.
//   3. Pokaz QR + backup codes + instrukcje ("zeskanuj QR, zapisz kody").
//   4. Input 6-cyfrowy TOTP code.
//   5. Submit → POST /auth/admin/2fa/verify {secret, totpCode, backupCodes}.
//   6. Response {enabled, admin} + Set-Cookie admin_session → refresh + navigate('/dashboard').
//
// UI ma 2 stepy:
//   - 'display': pokaz QR + backup codes + przycisk "Dalej".
//   - 'verify': input TOTP + "Potwierdz".
// Po submit 'verify' → navigate.
//
// Error states:
//   - Brak setupToken w location.state → redirect /login.
//   - /setup 401/expired → error + link "Wróć do logowania".
//   - /verify 401 INVALID_TOTP → inline error w formie.

import { type FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

interface SetupResponse {
  secret: string;
  qrDataUrl: string;
  backupCodes: string[];
}

type Step =
  | { kind: 'loading-setup' }
  | { kind: 'display'; secret: string; qrDataUrl: string; backupCodes: string[] }
  | {
      kind: 'verify';
      secret: string;
      backupCodes: string[];
    }
  | {
      kind: 'submitting-verify';
      secret: string;
      backupCodes: string[];
    }
  | { kind: 'error'; message: string };

export function Admin2FASetup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const setupToken = (location.state as { setupToken?: string } | null)?.setupToken;

  const [step, setStep] = useState<Step>({ kind: 'loading-setup' });
  const [totpCode, setTotpCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Load setup data on mount.
  useEffect(() => {
    if (!setupToken) {
      navigate('/login', { replace: true });
      return;
    }
    void (async () => {
      try {
        const { data } = await api.post<SetupResponse>(
          '/auth/admin/2fa/setup',
          {},
          { headers: { Authorization: `Bearer ${setupToken}` } },
        );
        setStep({
          kind: 'display',
          secret: data.secret,
          qrDataUrl: data.qrDataUrl,
          backupCodes: data.backupCodes,
        });
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        setStep({
          kind: 'error',
          message:
            status === 401
              ? 'Sesja setup wygasła lub jest nieważna. Zaloguj się ponownie.'
              : 'Nie udało się pobrać danych setupu 2FA',
        });
        console.error('2fa/setup failed', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownloadBackupCodes = () => {
    if (step.kind !== 'display') return;
    const content = [
      'SaaS v2 — Admin 2FA backup codes',
      `Generated: ${new Date().toISOString()}`,
      '',
      'IMPORTANT: each code works ONLY ONCE. Keep this file safe.',
      '',
      ...step.backupCodes,
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saas-admin-2fa-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onProceedToVerify = () => {
    if (step.kind !== 'display') return;
    setStep({
      kind: 'verify',
      secret: step.secret,
      backupCodes: step.backupCodes,
    });
  };

  const onSubmitVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'verify') return;
    setVerifyError(null);
    const current = step;
    setStep({
      kind: 'submitting-verify',
      secret: current.secret,
      backupCodes: current.backupCodes,
    });

    try {
      await api.post(
        '/auth/admin/2fa/verify',
        {
          secret: current.secret,
          totpCode,
          backupCodes: current.backupCodes,
        },
        { headers: setupToken ? { Authorization: `Bearer ${setupToken}` } : {} },
      );
      await refresh();
      navigate('/dashboard');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setVerifyError(
        status === 401
          ? 'Kod TOTP niepoprawny. Spróbuj ponownie.'
          : 'Weryfikacja nieudana — spróbuj ponownie.',
      );
      setStep(current); // Revert back to 'verify' state.
      console.error('2fa/verify failed', err);
    }
  };

  // --- Render ---

  if (step.kind === 'loading-setup') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-text-tertiary">Przygotowuję setup 2FA…</p>
        </div>
      </main>
    );
  }

  if (step.kind === 'error') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
          <h1 className="mb-4 text-xl font-bold text-text-primary">Błąd setupu 2FA</h1>
          <p className="mb-6 text-sm text-text-secondary">{step.message}</p>
          <Button className="w-full" onClick={() => navigate('/login')}>
            Wróć do logowania
          </Button>
        </div>
      </main>
    );
  }

  if (step.kind === 'display') {
    return (
      <main className="flex min-h-screen items-start justify-center bg-bg-secondary p-4 py-8">
        <div className="w-full max-w-2xl rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-text-primary">Skonfiguruj 2FA</h1>
            <p className="mt-2 text-sm text-text-tertiary">
              Bezpieczeństwo konta admina wymaga drugiego czynnika uwierzytelniania.
            </p>
          </div>

          <div className="mb-6 rounded-lg border border-border-secondary bg-bg-secondary p-4">
            <h2 className="mb-3 font-semibold text-text-primary">Krok 1 — Zeskanuj QR</h2>
            <p className="mb-3 text-sm text-text-tertiary">
              Otwórz aplikację Authenticator (Google Authenticator, Authy, 1Password) i zeskanuj kod
              QR poniżej.
            </p>
            <div className="flex justify-center rounded-lg bg-white p-4">
              <img
                src={step.qrDataUrl}
                alt="QR code do 2FA"
                className="h-48 w-48"
                data-testid="qr-image"
              />
            </div>
            <p className="mt-3 text-xs text-text-muted">
              Nie możesz zeskanować? Wpisz secret ręcznie:{' '}
              <code className="rounded bg-bg-tertiary px-2 py-0.5 font-mono text-xs text-text-primary">
                {step.secret}
              </code>
            </p>
          </div>

          <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
            <h2 className="mb-3 font-semibold text-text-primary">
              Krok 2 — Zapisz kody zapasowe ⚠️
            </h2>
            <p className="mb-3 text-sm text-text-secondary">
              Te kody działają <strong>jednokrotnie</strong> w razie utraty telefonu. Zapisz je w
              bezpiecznym miejscu (menedżer haseł, sejf, wydruk). Po zamknięciu tej strony{' '}
              <strong>nie będziesz mógł ich odzyskać</strong>.
            </p>
            <div className="mb-3 grid grid-cols-2 gap-2 font-mono text-sm">
              {step.backupCodes.map((code) => (
                <div key={code} className="rounded bg-bg-elevated px-3 py-2 text-text-primary">
                  {code}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={onDownloadBackupCodes}
              className="w-full"
            >
              Pobierz kody jako plik .txt
            </Button>
          </div>

          <Button className="w-full" onClick={onProceedToVerify}>
            Zapisałem/am — dalej do weryfikacji
          </Button>
        </div>
      </main>
    );
  }

  // step.kind === 'verify' | 'submitting-verify'
  const loading = step.kind === 'submitting-verify';
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <h1 className="mb-2 text-2xl font-bold text-text-primary">Krok 3 — Potwierdź kod</h1>
        <p className="mb-6 text-sm text-text-tertiary">
          Otwórz Authenticator i wpisz 6-cyfrowy kod dla konta admin.
        </p>

        <form onSubmit={onSubmitVerify} className="space-y-4">
          <Input
            label="Kod 6-cyfrowy"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            required
            autoFocus
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            disabled={loading}
          />

          {verifyError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {verifyError}
            </div>
          )}

          <Button type="submit" className="w-full" loading={loading}>
            {loading ? 'Weryfikacja…' : 'Potwierdź i zaloguj'}
          </Button>
        </form>
      </div>
    </main>
  );
}
